<template>
  <div class="page-container">
    <PageHeader title="试题管理" show-breadcrumb />

    <!-- 搜索面板：分类/类型/题名 -->
    <SearchPanel>
      <el-select v-model="query.qtcId" placeholder="题目分类" clearable size="small" filterable>
        <el-option v-for="item in classList" :key="item.qtcId" :label="item.qtcName" :value="item.qtcId" />
      </el-select>
      <el-select v-model="query.qttId" placeholder="试题类型" clearable size="small" filterable>
        <el-option v-for="item in typeList" :key="item.qttId" :label="item.qttName" :value="item.qttId" />
      </el-select>
      <el-input v-model.trim="query.qtName" placeholder="题目名称" clearable size="small" @keyup.enter.native="handleSearch" />
      <template #actions>
        <el-button type="primary" icon="el-icon-search" size="small" @click="handleSearch">查询</el-button>
        <el-button icon="el-icon-refresh" size="small" @click="handleReset">重置</el-button>
        <el-button type="success" icon="el-icon-plus" size="small" @click="handleAdd">新增试题</el-button>
        <el-button type="warning" icon="el-icon-upload2" size="small" @click="openImport">批量导入</el-button>
      </template>
    </SearchPanel>

    <TablePanel title="试题列表">
      <el-table :data="list" v-loading="loading" size="small" stripe :height="tableHeight">
        <el-table-column type="index" label="序号" width="80" align="center" />
        <el-table-column prop="qtName" label="题干" min-width="280" align="center" show-overflow-tooltip />
        <el-table-column prop="qtcName" label="分类" min-width="130" align="center" show-overflow-tooltip />
        <el-table-column prop="qttName" label="类型" min-width="110" align="center" />
        <el-table-column prop="qtScore" label="分值" min-width="90" align="center" />
        <el-table-column label="操作" width="220" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" style="color:#67c23a" @click="handleDetail(row)">详情</el-button>
            <el-button type="text" size="mini" style="color:#409eff" @click="handleEdit(row)">编辑</el-button>
            <el-button type="text" size="mini" style="color:#e6a23c" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        :current-page="page.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="page.pageSize"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </TablePanel>

    <!-- 新增/编辑试题弹框（按题型动态展示选项组件） -->
    <el-dialog :title="dialogType === 'add' ? '新增试题' : '编辑试题'" :visible.sync="dialogVisible" width="780px" v-dialog-drag-resize>
      <el-form ref="formRef" v-loading="editLoading" :model="form" :rules="rules" label-width="90px" size="small">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="题目分类" prop="qtcId">
              <el-select v-model="form.qtcId" placeholder="请选择分类" filterable style="width:100%">
                <el-option v-for="item in classList" :key="item.qtcId" :label="item.qtcName" :value="item.qtcId" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="试题类型" prop="qttId">
              <el-select v-model="form.qttId" placeholder="请选择类型" filterable style="width:100%" @change="handleTypeChange">
                <el-option v-for="item in typeList" :key="item.qttId" :label="item.qttName" :value="item.qttId" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="题干" prop="qtName">
          <el-input v-model="form.qtName" type="textarea" :rows="3" placeholder="请输入题干内容" />
        </el-form-item>
        <el-form-item label="题干图片">
          <cj-upload :filearr="imageFileList" :limit="1" accept="image/*" @fileChange="onImageChange" @removeAfter="onImageRemove" />
        </el-form-item>
        <el-form-item label="分值">
          <el-input-number v-model="form.qtScore" :min="0" :max="100" controls-position="right" />
        </el-form-item>

        <!-- 填空题/解答题：参考答案输入框 -->
        <el-form-item v-if="form.qttId && topicKind === 'input'" label="参考答案">
          <el-input v-model="form.answerText" type="textarea" :rows="3" placeholder="请输入参考答案" />
        </el-form-item>

        <!-- 单选/判断/多选：选项设置 -->
        <el-form-item v-else-if="form.qttId" label="选项设置">
          <div class="topic-items">
            <!-- 单选 / 判断：radio 互斥（选中一个为正确答案） -->
            <el-radio-group v-if="topicKind === 'single' || topicKind === 'judge'" v-model="singleAnswerIdx" class="topic-radio-group">
              <div v-for="(item, idx) in form.items" :key="idx" class="topic-item-row">
                <el-radio class="item-answer" :label="idx">正确答案</el-radio>
                <el-input v-model.trim="item.qtiValue" placeholder="选项内容" size="small" class="item-value" :disabled="topicKind === 'judge'" />
                <el-button v-if="topicKind === 'single'" type="danger" icon="el-icon-delete" size="mini" circle :disabled="form.items.length <= 2" @click="removeItem(idx)" />
              </div>
            </el-radio-group>
            <!-- 多选：checkbox（多个正确答案） -->
            <div v-else>
              <div v-for="(item, idx) in form.items" :key="idx" class="topic-item-row">
                <el-checkbox class="item-answer" :value="item.qtiIsAnswer === 2" @change="val => (item.qtiIsAnswer = val ? 2 : 1)">正确答案</el-checkbox>
                <el-input v-model.trim="item.qtiValue" placeholder="选项内容" size="small" class="item-value" />
                <el-input v-model.trim="item.qtiKeys" placeholder="关键词（选填）" size="small" class="item-keys" />
                <el-button type="danger" icon="el-icon-delete" size="mini" circle :disabled="form.items.length <= 1" @click="removeItem(idx)" />
              </div>
            </div>
            <el-button v-if="topicKind === 'single' || topicKind === 'multiple'" type="primary" plain icon="el-icon-plus" size="mini" @click="addItem">添加选项</el-button>
            <div class="topic-items-tip">{{ tipText }}</div>
          </div>
        </el-form-item>

        <!-- 未选择类型时的占位提示 -->
        <el-form-item v-else label="选项设置">
          <div class="topic-items-tip">请先选择试题类型</div>
        </el-form-item>

        <el-form-item label="答案解析">
          <el-input v-model="form.qtAnalysis" type="textarea" :rows="3" placeholder="答案解析（选填）" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="submitLoading" @click="submitDialog">确定</el-button>
      </div>
    </el-dialog>

    <!-- 试题详情弹框（只读展示完整数据） -->
    <el-dialog title="试题详情" :visible.sync="detailVisible" width="720px" v-dialog-drag-resize>
      <div v-loading="detailLoading" class="topic-detail">
        <template v-if="detail">
          <div class="detail-meta">
            <span><b>分类：</b>{{ detail.qtcName || '-' }}</span>
            <span><b>类型：</b>{{ detail.qttName || '-' }}</span>
            <span><b>分值：</b>{{ detail.qtScore != null ? detail.qtScore : '-' }}</span>
          </div>
          <div class="detail-block">
            <div class="detail-label">题干</div>
            <div class="detail-text">{{ detail.qtName }}</div>
          </div>
          <div v-if="detail.qtNameImage" class="detail-block">
            <div class="detail-label">题干图片</div>
            <el-image :src="detail.qtNameImage" fit="contain" class="detail-img" :preview-src-list="[detail.qtNameImage]" />
          </div>
          <div v-if="detail.questionTopicItemList && detail.questionTopicItemList.length" class="detail-block">
            <div class="detail-label">选项 / 答案</div>
            <div v-for="(opt, idx) in detail.questionTopicItemList" :key="idx" class="detail-option">
              <span>{{ opt.qtiValue }}</span>
              <el-tag v-if="Number(opt.qtiIsAnswer) === 2" type="success" size="mini">正确答案</el-tag>
            </div>
          </div>
          <div v-if="detail.qtAnalysis" class="detail-block">
            <div class="detail-label">答案解析</div>
            <div class="detail-text">{{ detail.qtAnalysis }}</div>
          </div>
        </template>
      </div>
      <div slot="footer">
        <el-button size="small" @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 批量导入弹框：拖拽/选择 Excel → 上传 → 展示成功/失败明细 -->
    <el-dialog title="批量导入试题" :visible.sync="importVisible" width="620px" v-dialog-drag-resize>
      <el-upload
        drag
        action="#"
        :auto-upload="true"
        :show-file-list="false"
        accept=".xlsx,.xls"
        :http-request="uploadImport"
        v-loading="importLoading"
      >
        <i class="el-icon-upload" />
        <div class="el-upload__text">将 Excel 文件拖到此处，或<em>点击选择</em> 即开始导入</div>
        <div slot="tip" class="el-upload__tip">仅支持 .xlsx / .xls；固定列：题目分类名称、试题类型名称、题目名称、分值、选项A-D、正确答案（如 B 或 BD）、题干图片、解析</div>
      </el-upload>
      <div v-if="importResult" class="import-result">
        <el-alert
          :title="'共 ' + importResult.total + ' 条：成功 ' + importResult.success + ' 条，失败 ' + importResult.fail + ' 条'"
          :type="importResult.fail ? 'warning' : 'success'"
          :closable="false"
          show-icon
        />
        <div v-if="importResult.errorList && importResult.errorList.length" class="import-errors">
          <div class="import-errors-title">失败明细：</div>
          <div v-for="(err, i) in importResult.errorList" :key="i" class="import-err-item">{{ err }}</div>
        </div>
      </div>
      <div slot="footer">
        <el-button size="small" icon="el-icon-download" @click="handleDownloadTemplate">下载模板</el-button>
        <el-button size="small" @click="importVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'
import CjUpload from '@/components/cj-upload.vue'
import { getZqpkQuestionTopicList, addZqpkQuestionTopic, updateZqpkQuestionTopic, deleteZqpkQuestionTopic, getZqpkQuestionTopicDetails, getZqpkQuestionTopicClassList, getZqpkQuestionTopicTypeList, importZqpkQuestionTopic, downloadZqpkQuestionTopicImportTemplate } from '@/api/api'

export default {
  name: 'QuestionTopic',
  components: { PageHeader, SearchPanel, TablePanel, CjUpload },
  data () {
    return {
      loading: false,
      tableHeight: 400,
      list: [],
      query: { qtcId: '', qttId: '', qtName: '' },
      page: { currentPage: 1, pageSize: 10, total: 0 },
      classList: [],
      typeList: [],
      dialogVisible: false,
      dialogType: 'add',
      submitLoading: false,
      form: this.buildEmptyForm(),
      // 单选/判断题：当前选中的正确答案索引（radio-group v-model）
      singleAnswerIdx: null,
      // 题干图片：cj-upload 的回显文件列表
      imageFileList: [],
      // 详情弹框
      detailVisible: false,
      detailLoading: false,
      detail: null,
      // 编辑加载中（调详情接口期间）
      editLoading: false,
      // 批量导入
      importVisible: false,
      importLoading: false,
      importResult: null,
      rules: {
        qtcId: [{ required: true, message: '请选择分类', trigger: 'change' }],
        qttId: [{ required: true, message: '请选择类型', trigger: 'change' }],
        qtName: [{ required: true, message: '请输入题干', trigger: 'blur' }]
      }
    }
  },
  computed: {
    // 按试题类型名称归类题型，决定选项区组件形态（不依赖具体 qttId 数值）
    topicKind () {
      const t = this.typeList.find(it => it.qttId === this.form.qttId)
      const name = (t && t.qttName) || ''
      if (name.includes('判断')) return 'judge'
      if (name.includes('多选')) return 'multiple'
      if (name.includes('填空') || name.includes('解答') || name.includes('问答') || name.includes('简答') || name.includes('论述')) return 'input'
      if (name.includes('单选')) return 'single'
      return 'single'
    },
    tipText () {
      if (this.topicKind === 'judge') return '判断题固定"正确/错误"两个选项，选择其中之一为正确答案。'
      if (this.topicKind === 'single') return '单选题：选择一个选项为正确答案。'
      if (this.topicKind === 'multiple') return '多选题：勾选一个或多个选项为正确答案。'
      return '请先选择试题类型'
    }
  },
  created () {
    this.fetchList()
    this.fetchClassList()
    this.fetchTypeList()
  },
  mounted () {
    this.$nextTick(this.calcTableHeight)
    window.addEventListener('resize', this.calcTableHeight)
  },
  beforeDestroy () { window.removeEventListener('resize', this.calcTableHeight) },
  methods: {
    // 构造空表单（含两个默认空选项）
    buildEmptyForm () {
      return {
        qtId: null,
        qtcId: '',
        qttId: '',
        qtName: '',
        qtNameImage: '',
        qtAnalysis: '',
        qtScore: 0,
        answerText: '',
        items: [this.buildEmptyItem(), this.buildEmptyItem()]
      }
    },
    buildEmptyItem () {
      return { qtiId: null, qtiValue: '', qtiIsAnswer: 1, qtiKeys: '' }
    },
    // 判断题固定选项（正确/错误，默认"正确"为答案）
    buildJudgeItems () {
      return [
        { qtiId: null, qtiValue: '正确', qtiIsAnswer: 2, qtiKeys: '' },
        { qtiId: null, qtiValue: '错误', qtiIsAnswer: 1, qtiKeys: '' }
      ]
    },
    calcTableHeight () {
      const el = this.$el.querySelector('.el-table')
      if (!el) return
      this.tableHeight = window.innerHeight - el.getBoundingClientRect().top - 80
    },
    async fetchList () {
      this.loading = true
      try {
        const params = { ...this.query, currentPage: this.page.currentPage, pageSize: this.page.pageSize }
        const res = await getZqpkQuestionTopicList(params)
        this.list = res.data || []
        this.page.total = res.total || 0
      } catch (e) { this.list = []; this.page.total = 0 } finally { this.loading = false }
    },
    async fetchClassList () {
      try {
        const res = await getZqpkQuestionTopicClassList({ currentPage: 1, pageSize: 999 })
        this.classList = res.data || []
      } catch (e) { this.classList = [] }
    },
    async fetchTypeList () {
      try {
        const res = await getZqpkQuestionTopicTypeList({ currentPage: 1, pageSize: 999 })
        this.typeList = res.data || []
      } catch (e) { this.typeList = [] }
    },
    handleSearch () { this.page.currentPage = 1; this.fetchList() },
    handleReset () { this.query = { qtcId: '', qttId: '', qtName: '' }; this.handleSearch() },
    handleSizeChange (size) { this.page.pageSize = size; this.handleSearch() },
    handlePageChange (page) { this.page.currentPage = page; this.fetchList() },
    addItem () { this.form.items.push(this.buildEmptyItem()) },
    removeItem (idx) {
      this.form.items.splice(idx, 1)
      // 单选/判断：维护 singleAnswerIdx 与删除位置的偏移
      if (this.topicKind === 'single' || this.topicKind === 'judge') {
        if (this.singleAnswerIdx === idx) this.singleAnswerIdx = null
        else if (this.singleAnswerIdx != null && idx < this.singleAnswerIdx) this.singleAnswerIdx--
      }
    },
    // 用户手动切换试题类型时，按题型重置选项区（编辑回显不走这里）
    handleTypeChange () {
      const kind = this.topicKind
      if (kind === 'judge') {
        this.form.items = this.buildJudgeItems()
        this.form.answerText = ''
        this.singleAnswerIdx = 0
      } else if (kind === 'input') {
        this.form.items = []
        this.singleAnswerIdx = null
      } else if (kind === 'single') {
        this.form.items = [this.buildEmptyItem(), this.buildEmptyItem()]
        this.singleAnswerIdx = null
      } else {
        // 多选
        this.form.items = [this.buildEmptyItem(), this.buildEmptyItem()]
        this.singleAnswerIdx = null
      }
    },
    // 编辑回显：根据题型还原选项/答案/单选索引
    restoreByKind (rawItems) {
      const kind = this.topicKind
      if (kind === 'input') {
        // 填空/解答：参考答案存为首个选项的 qtiValue
        this.form.answerText = rawItems[0] ? (rawItems[0].qtiValue || '') : ''
        this.form.items = []
        this.singleAnswerIdx = null
        return
      }
      if (kind === 'judge') {
        // 判断题：固定正确/错误，按后端答案 qtiValue 定位选中项
        const answer = rawItems.find(it => Number(it.qtiIsAnswer) === 2)
        this.form.items = this.buildJudgeItems()
        if (answer) {
          const idx = this.form.items.findIndex(it => it.qtiValue === answer.qtiValue)
          if (idx >= 0) {
            this.form.items[idx].qtiIsAnswer = 2
            this.singleAnswerIdx = idx
          } else {
            this.singleAnswerIdx = 0
          }
        } else {
          this.singleAnswerIdx = 0
        }
        return
      }
      // 单选 / 多选：归一化选项列表
      const items = rawItems.length
        ? rawItems.map(it => ({
          qtiId: it.qtiId || null,
          qtiValue: it.qtiValue || '',
          qtiIsAnswer: Number(it.qtiIsAnswer) || 1,
          qtiKeys: it.qtiKeys || ''
        }))
        : [this.buildEmptyItem(), this.buildEmptyItem()]
      this.form.items = items
      this.singleAnswerIdx = (kind === 'single') ? items.findIndex(it => it.qtiIsAnswer === 2) : null
    },
    // 题干图片上传成功回调：cj-upload 回传 url 数组，取第一张
    onImageChange (urls) {
      this.form.qtNameImage = (urls && urls[0]) || ''
    },
    // 题干图片删除回调
    onImageRemove () {
      this.form.qtNameImage = ''
    },
    handleAdd () {
      this.dialogType = 'add'
      this.form = this.buildEmptyForm()
      this.singleAnswerIdx = null
      this.imageFileList = []
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    async handleEdit (row) {
      this.dialogType = 'edit'
      // 先重置空表单并打开弹框，调详情接口期间显示 loading
      this.form = this.buildEmptyForm()
      this.imageFileList = []
      this.singleAnswerIdx = null
      this.dialogVisible = true
      this.editLoading = true
      try {
        // 调详情接口获取完整数据（含完整选项）后回显
        const res = await getZqpkQuestionTopicDetails({ qtId: row.qtId })
        const d = res.data || {}
        this.form = {
          qtId: d.qtId,
          qtcId: d.qtcId,
          qttId: d.qttId,
          qtName: d.qtName || '',
          qtNameImage: d.qtNameImage || '',
          qtAnalysis: d.qtAnalysis || '',
          qtScore: d.qtScore != null ? Number(d.qtScore) : 0,
          answerText: '',
          items: []
        }
        const rawItems = (d.questionTopicItemList && d.questionTopicItemList.length) ? d.questionTopicItemList : []
        // 题干图片回显：将已有 URL 包装为 cj-upload 的文件列表
        this.imageFileList = d.qtNameImage ? [{ url: d.qtNameImage, name: '题干图片' }] : []
        // form.qttId 赋值后 topicKind 即时生效，按题型回显
        this.restoreByKind(rawItems)
        this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
      } catch (e) {} finally {
        this.editLoading = false
      }
    },
    // 查看试题详情（只读弹框）
    async handleDetail (row) {
      this.detailVisible = true
      this.detailLoading = true
      this.detail = null
      try {
        const res = await getZqpkQuestionTopicDetails({ qtId: row.qtId })
        this.detail = res.data || null
      } catch (e) { this.detail = null } finally {
        this.detailLoading = false
      }
    },
    submitDialog () {
      this.$refs.formRef.validate(async valid => {
        if (!valid) return
        const kind = this.topicKind
        let itemPayload = []
        if (kind === 'input') {
          // 填空/解答：参考答案作为唯一选项提交
          const ans = (this.form.answerText || '').trim()
          if (!ans) {
            this.$message.warning('请输入参考答案')
            return
          }
          itemPayload = [{ qtiValue: ans, qtiIsAnswer: 2, qtiKeys: '' }]
        } else {
          const validItems = this.form.items.filter(it => it.qtiValue)
          if (!validItems.length) {
            this.$message.warning('请至少添加一个选项内容')
            return
          }
          if (kind === 'single' || kind === 'judge') {
            // 单选/判断：按 singleAnswerIdx 标记唯一答案
            if (this.singleAnswerIdx == null || !this.form.items[this.singleAnswerIdx] || !this.form.items[this.singleAnswerIdx].qtiValue) {
              this.$message.warning('请选择一个正确答案')
              return
            }
            this.form.items.forEach((it, i) => { it.qtiIsAnswer = (i === this.singleAnswerIdx) ? 2 : 1 })
          } else {
            // 多选：至少勾选一个答案
            if (!validItems.some(it => it.qtiIsAnswer === 2)) {
              this.$message.warning('请至少勾选一个正确答案')
              return
            }
          }
          itemPayload = this.form.items.filter(it => it.qtiValue).map(it => {
            const o = { qtiValue: it.qtiValue, qtiIsAnswer: it.qtiIsAnswer, qtiKeys: it.qtiKeys || '' }
            // 编辑时携带已有选项 id，便于后端差量更新
            if (it.qtiId) o.qtiId = it.qtiId
            return o
          })
        }
        // 选项序列化为后端约定的 JSON 字符串字段 questionTopicItemListString
        const payload = {
          qtcId: this.form.qtcId,
          qttId: this.form.qttId,
          qtName: this.form.qtName,
          qtNameImage: this.form.qtNameImage,
          qtAnalysis: this.form.qtAnalysis,
          qtScore: this.form.qtScore,
          questionTopicItemListString: JSON.stringify(itemPayload)
        }
        this.submitLoading = true
        try {
          if (this.dialogType === 'add') {
            await addZqpkQuestionTopic(payload)
            this.$message.success('新增成功')
          } else {
            payload.qtId = this.form.qtId
            await updateZqpkQuestionTopic(payload)
            this.$message.success('编辑成功')
          }
          this.dialogVisible = false
          this.fetchList()
        } catch (e) {} finally {
          this.submitLoading = false
        }
      })
    },
    async handleDelete (row) {
      try { await this.$confirm('确定删除该试题？', '提示', { type: 'warning' }) } catch { return }
      try {
        await deleteZqpkQuestionTopic({ qtId: row.qtId })
        this.$message.success('删除成功')
        this.fetchList()
      } catch (e) {}
    },
    // 下载批量导入模板（Excel 文件流，触发浏览器下载；兼容后端鉴权失败返回的 JSON 错误体）
    async handleDownloadTemplate () {
      try {
        const blob = await downloadZqpkQuestionTopicImportTemplate()
        if (blob && blob.type && blob.type.indexOf('json') > -1) {
          const txt = await blob.text()
          let msg = '下载失败'
          try { msg = (JSON.parse(txt)).msg || msg } catch (e) {}
          this.$message.error(msg)
          return
        }
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = '试题批量导入模板.xlsx'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } catch (e) {}
    },
    // 打开批量导入弹框
    openImport () {
      this.importVisible = true
      this.importResult = null
    },
    // 选择/拖入文件即上传导入（el-upload http-request 自定义上传）
    async uploadImport (options) {
      const file = options && options.file
      if (!file) return
      const fd = new FormData()
      fd.append('file', file)
      this.importLoading = true
      this.importResult = null
      try {
        const res = await importZqpkQuestionTopic(fd)
        // res.data: { total, success, fail, errorList }
        const sum = (res && res.data) || { total: 0, success: 0, fail: 0, errorList: [] }
        this.importResult = sum
        if (sum.success > 0) {
          this.$message.success('导入完成，成功 ' + sum.success + ' 条' + (sum.fail ? '，失败 ' + sum.fail + ' 条' : ''))
          this.fetchList()
        } else {
          // 全部失败：明细已在弹窗展示，这里给一条汇总提示
          this.$message.warning('导入失败 ' + sum.fail + ' 条，请查看下方失败明细')
        }
      } catch (e) {
        // 拦截器已对携带 data 的 error 透传，正常不会到这里；网络异常兜底
        this.$message.error('导入失败，请稍后重试')
      } finally {
        this.importLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.topic-items {
  width: 100%;
}
.topic-radio-group {
  width: 100%;
  display: block;
}
.topic-item-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.item-answer {
  flex-shrink: 0;
  width: 90px;
}
.item-value {
  flex: 1;
}
.item-keys {
  width: 160px;
  flex-shrink: 0;
}
.topic-items-tip {
  color: #909399;
  font-size: 12px;
  margin-top: 6px;
  line-height: 1.6;
}
.topic-detail { min-height: 120px; }
.detail-meta {
  display: flex;
  gap: 24px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #475569;
}
.detail-block { margin-bottom: 16px; }
.detail-label {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}
.detail-text {
  font-size: 14px;
  color: #334155;
  line-height: 1.8;
  padding: 10px 12px;
  background: #fafbfc;
  border-radius: 6px;
}
.detail-img { max-width: 100%; max-height: 240px; border-radius: 8px; }
.detail-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fafbfc;
  border-radius: 6px;
  margin-bottom: 6px;
  font-size: 13px;
  color: #334155;
}
/* 批量导入结果 */
.import-result { margin-top: 12px; }
.import-errors {
  max-height: 200px;
  overflow-y: auto;
  margin-top: 10px;
  padding: 10px 12px;
  background: #fef0f0;
  border-radius: 6px;
  border: 1px solid #fde2e2;
}
.import-errors-title {
  font-size: 13px;
  font-weight: 600;
  color: #f56c6c;
  margin-bottom: 6px;
}
.import-err-item {
  font-size: 12px;
  color: #b34545;
  line-height: 1.7;
}
</style>
